"use client"

import type React from "react"
import { PlayIcon, RedoIcon, VolumeIcon, VolumeMutedIcon, MusicIcon } from "@/components/icons/pixel-icons"
import { useAudioSettings, setAudioSettings } from "@/lib/audio-settings"

interface PauseMenuProps {
  onResume: () => void
  onRestart: () => void
}

/** One labelled volume row: icon toggles mute, slider sets the level */
const VolumeRow = ({
  label,
  icon,
  mutedIcon,
  value,
  onChange,
}: {
  label: string
  icon: React.ReactNode
  mutedIcon: React.ReactNode
  value: number
  onChange: (value: number) => void
}) => (
  <div className="flex items-center gap-3">
    <button
      onClick={() => onChange(value > 0 ? 0 : 1)}
      className="text-foreground hover:text-foreground/70 transition-colors shrink-0 p-1"
      title={value > 0 ? `Mute ${label.toLowerCase()}` : `Unmute ${label.toLowerCase()}`}
      aria-label={value > 0 ? `Mute ${label.toLowerCase()}` : `Unmute ${label.toLowerCase()}`}
    >
      {value > 0 ? icon : mutedIcon}
    </button>
    <div className="flex-1">
      <div className="flex justify-between text-xs text-foreground/70 mb-1">
        <span>{label}</span>
        <span>{Math.round(value * 100)}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        className="w-full h-2 cursor-pointer accent-amber-400"
        aria-label={`${label} volume`}
      />
    </div>
  </div>
)

export const PauseMenu = ({ onResume, onRestart }: PauseMenuProps) => {
  const { sfx, music } = useAudioSettings()

  return (
    <div id="pause-menu" className="absolute inset-0 z-50 flex items-center justify-center bg-game-surface-overlay">
      <div className="bg-game-surface-elevated p-8 rounded-lg shadow-2xl border-2 border-game-border w-full max-w-xs">
        <h2 className="text-4xl font-bold text-foreground mb-6 text-center">Paused</h2>

        {/* Audio settings - persisted globally, applied immediately */}
        <div className="flex flex-col gap-4 mb-6 bg-black/30 border border-game-border rounded-lg p-4">
          <VolumeRow
            label="Sound"
            icon={<VolumeIcon size={20} />}
            mutedIcon={<VolumeMutedIcon size={20} />}
            value={sfx}
            onChange={(v) => setAudioSettings({ sfx: v })}
          />
          <VolumeRow
            label="Music"
            icon={<MusicIcon size={20} />}
            mutedIcon={<MusicIcon size={20} className="opacity-40" />}
            value={music}
            onChange={(v) => setAudioSettings({ music: v })}
          />
        </div>

        <div className="flex flex-col gap-4">
          <button
            id="resume-button"
            onClick={onResume}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-foreground hover:bg-foreground/90 text-background text-xl font-bold rounded-lg transition-all"
          >
            <PlayIcon size={24} />
            Resume
          </button>

          <button
            id="restart-from-pause-button"
            onClick={onRestart}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-transparent hover:bg-foreground/10 text-foreground text-xl font-bold rounded-lg transition-all border-2 border-foreground"
          >
            <RedoIcon size={24} />
            Restart
          </button>
        </div>
      </div>
    </div>
  )
}
